import { isUndefined } from '@aloft/toolkit-shared';
import {
  Flex,
  Icon,
  IconButton,
  InfoIcon,
  Modal,
  SizeProperty,
  Tooltip,
  Typography,
} from '@aloft/toolkit-sui';

import { useState } from 'react';
import { Control, FieldPath, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';

import {
  selectCompSequenceByListingType,
  selectCompTypes,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { usePropertyGridListingType } from '../PropertyGridListingTypeContext.tsx';
import { PropertyGridColumnDefinition, PropertyGridFieldName, PropertyGridForm } from '../types.ts';
import { handleUpdatePropertyData } from '../utils.ts';
import { readOnlyFieldTypographyProps } from './styles.ts';
import { PROPERTY_GRID_ROW_HEIGHT } from '../consts.ts';
import { PropertyGridFieldProps } from './types.ts';

const AutofillSubjectValueButton = ({
  fieldName,
  control,
  getValues,
  setValue,
}: {
  fieldName: PropertyGridFieldName;
  control: Control<PropertyGridForm>;
  getValues: UseFormGetValues<PropertyGridForm>;
  setValue: UseFormSetValue<PropertyGridForm>;
}) => {
  const dispatch = useAppDispatch();
  const listingType = usePropertyGridListingType();
  const compSequence = useAppSelector((state) =>
    selectCompSequenceByListingType(state, listingType),
  );
  const compTypes = useAppSelector(selectCompTypes);

  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const subjectValue: unknown = useWatch({
    control,
    name: `columns.0.propertyData.${fieldName}` as FieldPath<PropertyGridForm>,
  });
  const subjectArvFieldName = (
    (fieldName as string).startsWith('editableData.arv.')
      ? fieldName
      : (fieldName as string).replace(/^editableData\./, 'editableData.arv.')
  ) as PropertyGridFieldName;

  const columns = getValues('columns');
  const getSubjectValueForComp = (compId: string): unknown => {
    const subjectPropertyData = getValues('columns.0.propertyData');
    const subjectArvValueForComp = getValues(
      `columns.0.propertyData.${subjectArvFieldName}` as FieldPath<PropertyGridForm>,
    );
    let pathValue: unknown = subjectPropertyData;
    const hasSeparateArvSource = (subjectArvFieldName as string).split('.').every((segment) => {
      if (!pathValue || typeof pathValue !== 'object') return false;
      if (!(segment in pathValue)) return false;
      pathValue = (pathValue as Record<string, unknown>)[segment];
      return true;
    });

    return compTypes?.[compId] === 'arv' && hasSeparateArvSource
      ? subjectArvValueForComp
      : subjectValue;
  };

  const compFields = compSequence
    .map((compId) => ({
      compId,
      columnIndex: columns.findIndex(
        (column: PropertyGridColumnDefinition) =>
          column?.propertyId === compId && column?.type === 'property',
      ),
    }))
    .filter((compField) => compField.columnIndex !== -1);

  const allCompsMatchSubject = compFields.every(
    ({ compId, columnIndex }) =>
      getValues(
        `columns.${columnIndex}.propertyData.${fieldName}` as FieldPath<PropertyGridForm>,
      ) === getSubjectValueForComp(compId),
  );

  const canAutoFill = compFields.every(({ compId, columnIndex }) => {
    const compValue = getValues(
      `columns.${columnIndex}.propertyData.${fieldName}` as FieldPath<PropertyGridForm>,
    );
    const sourceValue = getSubjectValueForComp(compId);

    return isUndefined(compValue) || compValue === sourceValue;
  });

  const handleAutofill = () => {
    compFields.forEach(({ compId, columnIndex }) => {
      const propertyData = getValues(`columns.${columnIndex}.propertyData`);
      if (propertyData) {
        setValue(
          `columns.${columnIndex}.propertyData.${fieldName}`,
          getSubjectValueForComp(compId) as never,
        );
        handleUpdatePropertyData(compId, getValues(), dispatch, propertyData);
      }
    });
  };

  const tooltipLabel = 'Paste Subj. Value Across Comps';
  const hasSourceValue = compFields.some(
    ({ compId }) => !isUndefined(getSubjectValueForComp(compId)),
  );
  const isDisabled = !hasSourceValue || allCompsMatchSubject;

  return (
    <>
      <Modal
        size="lg"
        isOpen={isConfirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
        closeOnEsc
        title="Existing Feature Values"
        primaryAction={{
          id: 'override-existing-values-btn',
          text: 'Override',
          onClick: () => {
            handleAutofill();
            setConfirmationModalOpen(false);
          },
        }}
        secondaryAction={{
          text: 'Cancel',
          onClick: () => setConfirmationModalOpen(false),
        }}
      >
        Some comparables already have a value set for this feature. Do you want to overwrite the
        existing values?
      </Modal>
      <Tooltip label={tooltipLabel}>
        <Flex
          paddingRight={isDisabled ? '8px' : '0'}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
        >
          {isDisabled && <Icon icon="autofill" color="strokeDefault" />}
          {!isDisabled && (
            <IconButton
              variant="tertiary"
              ariaLabel={tooltipLabel}
              icon="autofill"
              iconColor="textLight"
              size="xs"
              onClick={() => {
                if (canAutoFill) handleAutofill();
                else setConfirmationModalOpen(true);
              }}
            />
          )}
        </Flex>
      </Tooltip>
    </>
  );
};

export const LabelField = ({
  columnIndex,
  field: { fieldName, label, autofillSubjectValue, tooltipContent },
  control,
  getValues,
  setValue,
  required,
}: PropertyGridFieldProps) => (
  <Flex backgroundColor="surface2" height={PROPERTY_GRID_ROW_HEIGHT} alignItems="center" gap="4px" paddingRight="4px">
    <Flex padding={readOnlyFieldTypographyProps?.__dangerDoNotUseSx?.padding as SizeProperty}>
      <Typography {...readOnlyFieldTypographyProps} __dangerDoNotUseSx={{ padding: 0 }}>
        {label}
      </Typography>
      {required && <span style={{ color: 'red', marginLeft: '2px' }}> *</span>}
    </Flex>
    {tooltipContent && <InfoIcon tooltipContent={tooltipContent} />}
    {autofillSubjectValue && columnIndex === -1 && (
      <AutofillSubjectValueButton
        fieldName={fieldName}
        control={control}
        getValues={getValues}
        setValue={setValue}
      />
    )}
  </Flex>
);
